/**
 * CustomForecast Form — форма для кастомного прогноза
 *
 * Позволяет пользователю задать свободный вопрос и получить
 * персонализированный AI-ответ на основе натальной карты.
 */

import React, { useState } from 'react';
import api from '../api';

interface CustomForecastFormProps {
  onResult: (data: { text: string; question: string }) => void;
  onError: (message: string) => void;
}

const CustomForecastForm: React.FC<CustomForecastFormProps> = ({ onResult, onError }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const MAX_CHARS = 1000;
  const MIN_CHARS = 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (question.trim().length < MIN_CHARS) {
      onError('Задайте вопрос (минимум 10 символов)');
      return;
    }

    if (question.length > MAX_CHARS) {
      onError('Вопрос слишком длинный (максимум 1000 символов)');
      return;
    }

    setLoading(true);

    try {
      const res = await api.post<{ text: string }>('/forecasts/custom', {
        question: question.trim(),
      });

      if (res.success && res.data?.aiInterpretation) {
        onResult({
          text: res.data.aiInterpretation,
          question: question.trim(),
        });
        setQuestion('');
        setCharCount(0);
      } else {
        onError(res.message || 'Не удалось получить прогноз');
      }
    } catch (err: any) {
      console.error('Custom forecast error:', err);
      onError(err.response?.data?.message || 'Произошла ошибка при загрузке прогноза');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setQuestion(value);
    setCharCount(value.length);
  };

  const isOverLimit = charCount > MAX_CHARS;
  const isUnderLimit = charCount > 0 && charCount < MIN_CHARS;

  return (
    <div
      style={{
        background: 'rgba(13, 20, 40, 0.85)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.12)',
        borderRadius: '24px',
        padding: '28px',
        marginBottom: '20px',
        boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
        position: 'relative',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />

      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <span style={{ fontSize: '28px' }}>🔮</span>
        <div>
          <h3
            style={{
              fontSize: '18px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              margin: 0,
              background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
              backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}
          >
            Задайте свой вопрос
          </h3>
          <p
            style={{
              color: '#b0b0b0',
              fontSize: '13px',
              margin: '4px 0 0',
              fontFamily: 'var(--font-sans)',
            }}
          >
            AI ответит на основе вашей натальной карты
          </p>
        </div>
      </div>

      {/* Форма */}
      <form onSubmit={handleSubmit}>
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <textarea
            value={question}
            onChange={handleInputChange}
            placeholder="Например: Что ждёт меня в карьере в ближайшие 3 месяца? Стоит ли мне менять работу?"
            disabled={loading}
            rows={4}
            style={{
              width: '100%',
              padding: '16px',
              paddingRight: '60px',
              background: 'rgba(13, 20, 40, 0.6)',
              border: isOverLimit
                ? '1px solid #FF5252'
                : isUnderLimit
                  ? '1px solid #FFC107'
                  : '1px solid rgba(212, 175, 55, 0.15)',
              borderRadius: '12px',
              color: '#f0f0f0',
              fontSize: '15px',
              lineHeight: 1.6,
              fontFamily: 'var(--font-sans)',
              resize: 'vertical',
              minHeight: '100px',
              maxHeight: '300px',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = isOverLimit ? '#FF5252' : 'rgba(212, 175, 55, 0.5)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isOverLimit
                ? '#FF5252'
                : isUnderLimit
                  ? '#FFC107'
                  : 'rgba(212, 175, 55, 0.15)';
            }}
          />

          {/* Счётчик символов */}
          <span
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '16px',
              color: isOverLimit ? '#FF5252' : isUnderLimit ? '#FFC107' : '#666',
              fontSize: '12px',
              fontFamily: 'var(--font-sans)',
              pointerEvents: 'none',
            }}
          >
            {charCount}/{MAX_CHARS}
          </span>
        </div>

        {/* Подсказки */}
        {charCount === 0 && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: 'rgba(212, 175, 55, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(212, 175, 55, 0.1)',
            }}
          >
            <p style={{ color: '#b0b0b0', fontSize: '13px', margin: '0 0 8px', fontFamily: 'var(--font-sans)' }}>
              Примеры вопросов:
            </p>
            <ul style={{ color: '#888', fontSize: '12px', margin: 0, paddingLeft: '16px', lineHeight: 1.8 }}>
              <li>Что ждёт меня в карьере в ближайшие месяцы?</li>
              <li>Подходит ли сейчас время для начала отношений?</li>
              <li>Какие сферы жизни сейчас наиболее активны?</li>
              <li>Когда лучший период для переезда?</li>
            </ul>
          </div>
        )}

        {/* Кнопка */}
        <button
          type="submit"
          disabled={loading || charCount < MIN_CHARS || isOverLimit}
          style={{
            width: '100%',
            padding: '14px',
            background:
              loading || charCount < MIN_CHARS || isOverLimit
                ? 'rgba(212, 175, 55, 0.1)'
                : 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
            border: 'none',
            borderRadius: '12px',
            color:
              loading || charCount < MIN_CHARS || isOverLimit ? '#666' : '#0A0814',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor:
              loading || charCount < MIN_CHARS || isOverLimit ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-sans)',
            transition: 'all 0.2s ease',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid #666',
                  borderTopColor: '#d4af37',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              Генерация...
            </span>
          ) : (
            'Получить прогноз'
          )}
        </button>
      </form>

      {/* Анимация спиннера */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CustomForecastForm;
